import { Coordinates } from '../types';

const KARTVERKET_POINT_URL = 'https://ws.geonorge.no/kommuneinfo/v1/punkt';
const NOMINATIM_REVERSE_URL = 'https://nominatim.openstreetmap.org/reverse';

const regionCache = new Map<string, RegionInfo | null>();

export interface RegionInfo {
  county: string;
  county_slug: string;
  municipality: string;
  municipality_slug: string;
  county_number?: string;
  municipality_number?: string;
}

const makeCacheKey = (coords: Coordinates) =>
  `${coords.latitude.toFixed(5)},${coords.longitude.toFixed(5)}`;

export const toSlug = (value: string): string => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const normalizeCountyName = (value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.replace(/^fylke\s+/i, '').trim();
};

const parseKartverketRegion = (data: any): RegionInfo | null => {
  const municipality = String(data?.kommunenavn || '').trim();
  const county = normalizeCountyName(String(data?.fylkesnavn || ''));
  if (!municipality || !county) return null;

  const municipality_slug = toSlug(municipality);
  const county_slug = toSlug(county);
  if (!municipality_slug || !county_slug) return null;

  return {
    county,
    county_slug,
    municipality,
    municipality_slug,
    county_number: data?.fylkesnummer ? String(data.fylkesnummer) : undefined,
    municipality_number: data?.kommunenummer ? String(data.kommunenummer) : undefined
  };
};

const parseNominatimRegion = (data: any): RegionInfo | null => {
  const address = data?.address || {};
  const municipality = String(
    address.municipality || address.city || address.town || address.village || ''
  ).trim();
  const county = normalizeCountyName(String(address.county || address.state || ''));
  if (!municipality || !county) return null;

  const municipality_slug = toSlug(municipality);
  const county_slug = toSlug(county);
  if (!municipality_slug || !county_slug) return null;

  return {
    county,
    county_slug,
    municipality,
    municipality_slug
  };
};

export const reverseGeocodeRegion = async (coords: Coordinates): Promise<RegionInfo | null> => {
  const cacheKey = makeCacheKey(coords);
  if (regionCache.has(cacheKey)) return regionCache.get(cacheKey)!;

  try {
    const params = new URLSearchParams({
      nord: String(coords.latitude),
      ost: String(coords.longitude),
      koordsys: '4326'
    });
    const res = await fetch(`${KARTVERKET_POINT_URL}?${params.toString()}`, {
      headers: { Accept: 'application/json' }
    });
    if (res.ok) {
      const parsed = parseKartverketRegion(await res.json());
      if (parsed) {
        regionCache.set(cacheKey, parsed);
        return parsed;
      }
    }
  } catch (err) {
    console.warn('[regionEnrichment] Kartverket lookup failed', err);
  }

  try {
    const params = new URLSearchParams({
      format: 'jsonv2',
      lat: String(coords.latitude),
      lon: String(coords.longitude),
      zoom: '10'
    });
    const res = await fetch(`${NOMINATIM_REVERSE_URL}?${params.toString()}`, {
      headers: { Accept: 'application/json', 'Accept-Language': 'no' }
    });
    if (res.ok) {
      const parsed = parseNominatimRegion(await res.json());
      if (parsed) {
        regionCache.set(cacheKey, parsed);
        return parsed;
      }
    }
  } catch (err) {
    console.warn('[regionEnrichment] Nominatim fallback failed', err);
  }

  regionCache.set(cacheKey, null);
  return null;
};
